import { User } from 'state/user/UserState'

const timeStamp = () => {
    return `${new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
        timeZone: 'America/New_York',
    })
        .format(new Date())
        .replace(/,/g, '')} ET`
}

/**
 * Push GTM event when a page is viewed in the Dealer Portal.
 *
 * @param {User} user - object of the current user
 */
export function pushPageView(user: User | undefined, pageType: string) {
    window.dataLayer.push({
        event: 'pageview',
        page: {
            page_location: window.location.pathname,
            page_referrer: window.document.referrer,
            page_type: `Dealer Portal - ${pageType}`,
        },
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a search query is entered.
 *
 * @param {string} searchQuery - string of query in search bar
 */
export function pushLeadsSearchQuery(searchQuery: string) {
    window.dataLayer.push({
        event: 'leads_search_query',
        search_query: searchQuery,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a filter is set and saved.
 *
 * @param {string} filter - string of clicked filter
 */
export function pushFilterSet(filter: string) {
    window.dataLayer.push({
        event: 'filter_set',
        filter_query: filter,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the View Lead button is clicked in the main table.
 *
 * @param {number} leadId - selected lead number
 */
export function pushViewLeadClick(leadId: number) {
    window.dataLayer.push({
        event: 'view_lead_click',
        lead_id: leadId,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Convert To App button is clicked in the main table.
 *
 * @param {number} leadId - selected lead number
 */
export function pushConvertToAppClick(leadId: number) {
    window.dataLayer.push({
        event: 'convert_to_app_click_main',
        lead_id: leadId,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a pagination button is pressed.
 *
 * @param {string} direction - string with pagination direction
 * @param {number} page - number of following page at time of click
 */
export function pushLeadPagination(direction: string, page: number) {
    window.dataLayer.push({
        event: 'lead_pagination',
        paging_page: page,
        paging_direction: direction,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a column sorting option is clicked.
 *  *
 * @param {string} expression - string with sorting expression
 */
export function pushSortingClick(expression: string) {
    window.dataLayer.push({
        event: 'sorting_click',
        sort_expression: expression,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a lead has been viewed.
 *
 * @param {number} leadId - selected lead number
 */
export function pushViewLeadModalImpression(leadId: number) {
    window.dataLayer.push({
        event: 'view_lead_modal_impression',
        lead_id: leadId,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Convert To App button is clicked within the modal.
 *
 * @param {number} leadId - converted lead number
 */
export function pushConvertToAppModalClick(leadId: number) {
    window.dataLayer.push({
        event: 'convert_to_app_click_modal',
        lead_id: leadId,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when a lead modal is closed.
 *
 * @param {number} leadId - closed lead number
 */
export function pushLeadModalClosed(leadId: number) {
    window.dataLayer.push({
        event: 'view_lead_modal_closed',
        lead_id: leadId,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Skip button is clicked in the Performance Dashboard tour modal.
 *
 * @param {User} user - object of the current user
 */
export function pushPerformanceTourSkipClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'performance_tour_skip_click',
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Start Tour button is clicked in the Performance Dashboard tour modal.
 *
 * @param {User} user - object of the current user
 */
export function pushPerformanceTourStartClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'performance_tour_start_click',
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Work New Leads button is clicked in the Performance Dashboard.
 *
 * @param {User} user - object of the current user
 */
export function pushWorkNewLeadsClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'work_new_leads_click',
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the Work Open Approvals button is clicked in the Performance Dashboard.
 *
 * @param {User} user - object of the current user
 */
export function pushWorkOpenApprovalsClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'work_open_approvals_click',
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the View Apps button is clicked in the Ready to Fund Apps tile in the Performance Dashboard.
 *
 * @param {User} user - object of the current user
 */
export function pushReadyToFundViewAppsClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'ready_to_fund_view_apps_click',
        user,
        time_stamp: timeStamp(),
    })
}

/**
 * Push a GTM event when the View Expiring Apps button is clicked in the Expiring Apps tile in the Performance Dashboard.
 *
 * @param {User} user - object of the current user
 */
export function pushExpiringAppsViewClick(user: User | undefined) {
    window.dataLayer.push({
        event: 'expiring_apps_view_click',
        user,
        time_stamp: timeStamp(),
    })
}
